import { useLocalization } from "@fluent/react";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { ToastValue, ToastsContext } from "./toastsContext";

interface ToastsContextProviderProps {
  children: ReactNode;
}

export const ToastsContextProvider = ({ children }: ToastsContextProviderProps) => {
  const { l10n } = useLocalization();
  const [toastsState, setToastsState] = useState<ToastValue[]>([]);

  const showToast = useCallback((toast: ToastValue) => {
    setToastsState((currentToasts) => {
      if (!currentToasts.every((n) => n.type !== toast.type || n.text !== toast.text)) {
        return currentToasts;
      }

      return [...currentToasts, toast];
    });
  }, []);

  const dismissToast = useCallback((toast: ToastValue) => {
    setToastsState((currentToasts) =>
      currentToasts.filter((a) => a.type !== toast.type || a.text !== toast.text)
    );
  }, []);

  const clearToasts = useCallback(() => {
    setToastsState((currentToasts) => (currentToasts.length === 0 ? currentToasts : []));
  }, []);

  const showGenericErrorToast = useCallback(() => {
    showToast({
      type: "negative",
      text: l10n.getString(
        "error-generic",
        null,
        "Something went wrong. Please try again or contact Support."
      ),
    });
  }, [l10n, showToast]);

  const showErrorToast = useCallback(
    (text: string) => {
      showToast({
        type: "negative",
        text,
      });
    },
    [showToast]
  );

  const value = useMemo(
    () => ({
      toastsState,
      showToast,
      showGenericErrorToast,
      clearToasts,
      dismissToast,
      showErrorToast,
    }),
    [toastsState, showToast, showGenericErrorToast, clearToasts, dismissToast, showErrorToast]
  );

  return <ToastsContext.Provider value={value}>{children}</ToastsContext.Provider>;
};
